import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, Navigate, Outlet, ScrollRestoration, useLocation, useMatch } from 'react-router-dom';

import AccessControl from '../../services/AccessControl/AccessControl.module';
import QueryToken from '../../services/QueryToken';
import useFreemiumGuard from '../../services/useFreemiumGuard';
import { refreshCompanyData, setIsBackOffice } from '../../store/AuthReducer';
import { useAppDispatch } from '../../store/hook';
import { setVigilanceStats } from '../../store/ModuleReducer';
import type { RootState } from '../../store/store';
import classNames from '../../utils/classNames';
import { JWTHeader } from '../../utils/JWTHeader';
import { FREEMIUM_ROUTES, isPublicRoute, PUBLIC_ROUTES, ROUTES } from '../../utils/routes';
import { ROUTE_TITLES } from '../../utils/titles';
import AdminSidebar from '../AdminSidebar';
import Footer from '../common/Footer';
import ToastrNotif from '../common/Notification/';
import Header from '../Header';
import PublicHeader from '../Header/PublicHeader';
import Breadcrumb from './components/Breadcrumb';


function MainPanel() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isBackOffice = useSelector((state: RootState) => state.auth.isBackOffice);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const isRegistered = useMemo(() => {
    const allRoutes = Object.values(ROUTES);
    return allRoutes.some(route => matchPath(route, location.pathname));
  }, [location.pathname]);

  const isAdminPage = useMemo(() => {
    const adminRoutes = Object.values(ROUTES).filter(route => route.includes('/admin'));
    return adminRoutes.some(route => matchPath(route, location.pathname));
  }, [location.pathname]);

  // The simplified setup dashboard renders its own hero banner with progress info,
  // so we hide the global breadcrumb hero on that page to avoid a double banner.
  const hideBreadcrumb = useMemo(
    () => Boolean(matchPath({ path: ROUTES.SETUP_SIMPLIFIED_DASHBOARD, end: true }, location.pathname)),
    [location.pathname],
  );

  const isBlocked = useFreemiumGuard([...FREEMIUM_ROUTES, ...PUBLIC_ROUTES]);
  const isCurrentRoutePublic = isPublicRoute(location.pathname);

  useEffect(() => {
    document.body.classList.toggle('bg-rgpd-admin', isBackOffice);
  }, [isBackOffice]);

  useEffect(() => {
    dispatch(setIsBackOffice(isAdminPage));

    const routeTitle = ROUTE_TITLES[location.pathname] || 'Déclarations Juridiques';
    document.title = `Déclarations Juridiques - ${routeTitle}`;
  }, [location, dispatch, isAdminPage]);

  useEffect(() => {
    if (!company?.id || !jwt) {return;}

    const fetchVigilanceStats = async() => {
      try {
        const response = await fetch(`/api/companies/${company.id}/vigilances/stats`, JWTHeader(jwt));
        if (!response.ok) {throw new Error('Network response was not ok');}
        const data = await response.json();
        dispatch(setVigilanceStats(data));
      } catch (error) {
        console.error('Error fetching vigilance stats :', error);
      }
    };

    fetchVigilanceStats();
  }, [company, jwt, dispatch]);

  useEffect(() => {
    if (company?.id && jwt) {
      const fetchCompanyData = async() => {
        try {
          const response = await fetch(`/api/companies/${company.id}`, JWTHeader(jwt));
          if (response.ok) {
            const freshData = await response.json();
            dispatch(refreshCompanyData(freshData));
          }
        } catch (error) {
          console.error('Error refreshing company data:', error);
        }
      };
      fetchCompanyData();
    }
  }, [company?.id, jwt, dispatch]);

  const isAccessBlockedPage = useMatch(ROUTES.BLOCKED_ACCESS);

  if (isBlocked) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <QueryToken />
      <div className="flex max-h-dvh flex-col overflow-hidden">
        <ToastrNotif />
        {/* Admin Sidebar - only shown on desktop in back office mode */}
        {isBackOffice && <AdminSidebar />}
        
        {(isRegistered && !isAccessBlockedPage) && (
          <div className={classNames(!isBackOffice && 'bg-white', isBackOffice && 'bg-rgpd-admin text-rgpd-white')}>
            {isCurrentRoutePublic ? <PublicHeader /> : <Header />}
          </div>
        )}
        <div 
          id={'mainScrollable'} 
          className={classNames(
            'overflow-y-auto flex-1',
            // Add left margin on desktop when in back office to account for sidebar width
            isBackOffice && 'lg:ml-64',
          )}
        >
          <main className={classNames('p-4 lg:p-8', isBackOffice ? 'bg-rgpd-admin' : 'bg-rgpd-page')}>
            <div className="mx-auto mb-4 flex-1">
              {!isBackOffice && !isCurrentRoutePublic && !hideBreadcrumb && (
                  <Breadcrumb />
                )}
              <AccessControl
                navigationList={Object.values(ROUTES)}
                navigationListBO={Object.values(ROUTES).filter(route => route.includes('/admin'))}
                >
                <Outlet />
              </AccessControl>
            </div>
            {isRegistered && !isAccessBlockedPage && <Footer />}
          </main>
        </div>
        <ScrollRestoration />
      </div>
    </>
  );
}

export default MainPanel;